
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Message, KitMessage } from "@/types/ticker";

export function useMessages(kitId: string | null) {
  const queryClient = useQueryClient();

  // Query to fetch all available messages
  const { data: allMessages = [] } = useQuery({
    queryKey: ['all-messages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as Message[];
    },
  });

  // Query to fetch kit-specific message associations
  const { data: kitMessages = [] } = useQuery({
    queryKey: ['kit-messages', kitId],
    queryFn: async () => {
      if (!kitId) return [];
      const { data, error } = await supabase
        .from('kit_messages')
        .select(`
          *,
          message:messages(*)
        `)
        .eq('kit_id', kitId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as KitMessage[];
    },
    enabled: !!kitId,
  });

  const addMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      // First create the message
      const { data: messageData, error: messageError } = await supabase
        .from('messages')
        .insert([{ text }])
        .select()
        .single();

      if (messageError) throw messageError;

      // If we have a kitId, associate the message with the kit
      if (kitId) {
        const { error: kitMessageError } = await supabase
          .from('kit_messages')
          .insert([{ 
            kit_id: kitId, 
            message_id: messageData.id,
            is_active: true 
          }]);
        
        if (kitMessageError) throw kitMessageError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-messages'] });
      if (kitId) {
        queryClient.invalidateQueries({ queryKey: ['kit-messages', kitId] });
      }
    },
  });

  const toggleMessageActiveMutation = useMutation({
    mutationFn: async ({ messageId, isActive }: { messageId: string; isActive: boolean }) => {
      if (!kitId) throw new Error("No kit selected");
      
      const { error } = await supabase
        .from('kit_messages')
        .upsert({ 
          kit_id: kitId,
          message_id: messageId,
          is_active: isActive 
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kit-messages', kitId] });
    },
  });

  const removeMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      // First remove the kit association if it exists
      if (kitId) {
        const { error: kitMessageError } = await supabase
          .from('kit_messages')
          .delete()
          .eq('kit_id', kitId)
          .eq('message_id', messageId);
        
        if (kitMessageError) throw kitMessageError;
      }

      // Then remove the message itself
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-messages'] });
      if (kitId) {
        queryClient.invalidateQueries({ queryKey: ['kit-messages', kitId] });
      }
    },
  });

  const updateMessageMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { error } = await supabase
        .from('messages')
        .update({ text })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-messages'] });
      if (kitId) {
        queryClient.invalidateQueries({ queryKey: ['kit-messages', kitId] });
      }
    },
  });

  return {
    messages: kitMessages.map(km => ({
      id: km.message.id,
      text: km.message.text,
      is_active: km.is_active
    })),
    allMessages,
    addMessageMutation,
    updateMessageMutation,
    toggleMessageActiveMutation,
    removeMessageMutation,
  };
}
